// Each roadmap level's preview phrase, as notation, so it can be drawn at any width.
//
// A bar is written one staff at a time as space-separated events:
//   C4/q    a pitch (step and octave) and a duration: w, h, q or e; a trailing "." dots it
//   r/h     a rest
//   R       a rest that fills the whole bar
//   -r/q    a leading "-" hides the event (the rests the picture never showed)
//   E4/q@3  "@n" puts fingering n on the note (above the treble staff, below the bass)
// Fingerings are the phrase set's own: its MusicXML writes each one as a text mark next
// to the note, and the transcribed levels copy the ones the old picture shows.
// Level 0 has no preview.
export interface PreviewPhraseData {
  // "beats/beat-type", e.g. "3/4"
  time: string;
  treble: string[];
  bass: string[];
}

export const previewPhrases: Readonly<Record<number, PreviewPhraseData>> = {
  // tier 1 phrase 54 of the level's phrase set
  1: {
    time: '4/4',
    treble: ['-R', '-R', '-R', '-R'],
    bass: ['C4/w', 'C4/q C4/h C4/q', 'C4/h C4/q C4/q', 'C4/h C4/h'],
  },
  // tier 1 phrase 21 of the level's phrase set (current version)
  2: {
    time: '4/4',
    treble: ['C4/q D4/h C4/q', 'D4/q C4/h D4/q', 'C4/q D4/h C4/q', 'C4/h C4/h'],
    bass: ['-R', '-R', '-R', '-R'],
  },
  // tier 2 phrase 59 of the level's phrase set
  3: {
    time: '4/4',
    treble: ['D4/h D4/h', '-r/h -r/q D4/q', '-r/h -r/q D4/q', '-r/q D4/q -r/h'],
    bass: ['-R', 'B3/q B3/q C4/q -r/q', 'C4/q B3/h -r/q', 'C4/q -r/q B3/q C4/q'],
  },
  // tier 1 phrase 15 of the level's phrase set
  4: {
    time: '4/4',
    treble: ['E4/q E4/q E4/q C4/q', 'D4/q D4/q D4/h', 'C4/q C4/q D4/h', 'E4/q C4/q C4/h'],
    bass: ['-R', '-R', '-R', '-R'],
  },
  // tier 2 phrase 68 of the level's phrase set
  5: {
    time: '4/4',
    treble: ['E4/q -r/q -r/h', 'E4/q -r/q -r/h', 'D4/h -r/q E4/q', 'D4/w'],
    bass: ['-r/q C4/q A3/q C4/q', '-r/q C4/q A3/q C4/q', '-r/h C4/q -r/q', '-R'],
  },
  // tier 1 phrase 63 of the level's phrase set
  6: {
    time: '3/4',
    treble: ['E4/q D4/q -r/q', '-r/q -r/q D4/q', 'E4/h -r/q', '-R'],
    bass: ['-r/q -r/q C4/q', 'B3/q C4/q -r/q', '-r/q -r/q C4/q', 'A3/h.'],
  },
  // tier 2 phrase 12 of the level's phrase set
  7: {
    time: '4/4',
    treble: ['-R', '-r/q C4/q D4/h', 'E4/q -r/q -r/h', 'D4/q -r/q -r/h'],
    bass: ['G3/q G3/q A3/q A3/q', 'B3/q -r/q -r/h', '-r/q B3/h B3/q', '-r/q A3/q B3/q G3/q'],
  },
  // tier 2 phrase 0 of the level's phrase set
  8: {
    time: '3/4',
    treble: ['C4/q D4/q E4/q', 'F4/h -r/q', 'E4/q F4/q D4/q', 'C4/q -r/q -r/q'],
    bass: ['-R', '-r/q -r/q A3/q', '-R', '-r/q G3/h'],
  },
  // tier 2 phrase 81 of the level's phrase set
  9: {
    time: '4/4',
    treble: ['D4/h E4/h', 'D4/h C4/h', 'C4/w', 'D4/w'],
    bass: ['B3/w', 'A3/w', 'G3/h A3/h', 'B3/w'],
  },
  // tier 3 phrase 98 of the level's phrase set
  10: {
    time: '3/4',
    treble: ['D4/h.', 'C4/h.', '-R', 'C4/q G4/q G4/q'],
    bass: ['B3/h G3/q', 'A3/h.', 'G3/q A3/q B3/q', '-R'],
  },
  // tier 3 phrase 94 of the level's phrase set
  11: {
    time: '4/4',
    treble: ['E4/w', '-r/h C4/q D4/q', 'E4/w', 'C4/h D4/h'],
    bass: ['C4/h. G3/q', 'F3/q A3/q -r/h', 'A3/h. G3/q', 'F3/w'],
  },
  // tier 2 phrase 0 of the level's phrase set
  12: {
    time: '4/4',
    treble: ['F4/h C4/h', 'D4/q E4/q F4/q G4/q', 'D4/h C4/h', 'E4/w'],
    bass: ['A3/h A3/h', 'B3/w', 'G3/h G3/h', 'A3/q C4/q B3/q G3/q'],
  },
  // tier 2 phrase 113 of the level's phrase set
  13: {
    time: '3/4',
    treble: ['C4/h E4/q', 'D4/h G4/q', 'E4/h D4/q', 'C4/h.'],
    bass: ['F3/q A3/h', 'G3/q B3/h', 'G3/h.', 'A3/h.'],
  },
  // tier 2 phrase 0 of the level's phrase set (current version)
  14: {
    time: '2/4',
    treble: ['E4/q F4/q', 'G4/q F4/q', 'E4/q C4/q', 'D4/h'],
    bass: ['C4/h', 'B3/q A3/q', 'G3/h', 'F3/h'],
  },
  // tier 2 phrase 95 of the level's phrase set (current version)
  15: {
    time: '4/4',
    treble: ['F4/h. r/q', 'r/q D4/q E4/q C4/q', 'r/q E4/h D4/q', 'C4/h C4/h'],
    bass: ['r/h C4/q B3/q', 'A3/w', 'F3/h G3/h', 'F3/q G3/q A3/h'],
  },
  // tier 4 phrase 52 of the level's phrase set
  16: {
    time: '3/4',
    treble: ['F4/q r/q E4/q', 'G4/q D4/h', 'A4/q F4/q D4/q', 'E4/h D4/q'],
    bass: ['r/q C4/q r/q', 'B3/h.', 'F3/h.', 'G3/q C4/q B3/q'],
  },
  // tier 2 phrase 0 of the level's phrase set (current version)
  17: {
    time: '3/4',
    treble: ['C4/q D4/q@2 A4/q@5', 'G4/h F4/q', 'E4/q F4/q G4/q', 'F4/q@3 C4/h@1'],
    bass: ['A3/h.', 'C4/h.', 'G3/h.', 'A3/h.'],
  },
  // tier 4 phrase 28 of the level's phrase set (current version)
  18: {
    time: '3/4',
    treble: ['F4/h D4/q', 'E4/q C4/q@1 r/q', 'C4/q F4/q@3 A4/q@5', 'G4/h.'],
    bass: ['A3/h.', 'G3/q r/q E3/q', 'F3/h.', 'G3/h B3/q'],
  },
  // transcribed from the earlier preview picture
  19: {
    time: '3/4',
    treble: ['C4/q@1 G4/q C4/q', 'D4/q@2 A4/q@5 D4/q', 'G4/q F4/q E4/q', 'E4/q@3 D4/q C4/q'],
    bass: ['F3/q A3/q F3/q', 'G3/q B3/q G3/q', 'B3/q@2 C4/h', 'C4/q@1 E3/h@5'],
  },
  // tier 2 phrase 0 of the level's phrase set (current version)
  20: {
    time: '2/4',
    treble: ['A4/q F4/q', 'B4/q@5 G4/q', 'A4/q E4/q', 'C4/q@1 E4/q'],
    bass: ['F3/h', 'G3/h', 'A3/h', 'A3/h'],
  },
  // transcribed from the earlier preview picture
  21: {
    time: '2/4',
    treble: ['F4/h', 'G4/q D4/q@2', 'C4/q D4/q', 'E4/h'],
    bass: ['D3/q@5 F3/q', 'G3/q r/q', 'A3/q@3 B3/q', 'C4/q G3/q'],
  },
  // transcribed from the earlier preview picture
  22: {
    time: '4/4',
    treble: ['C5/w', 'F4/w', 'C5/q G4/h B4/q', 'C5/q B4/q A4/q G4/q'],
    bass: ['r/q F3/q@5 A3/q F3/q', 'G3/h A3/q B3/q', 'C4/h. G3/q', 'A3/h A3/h'],
  },
  // tier 3 phrase 5 of the level's phrase set (current version). The left hand's "1" on
  // C4 in bar 2 is in the current phrase set but not in the old picture.
  23: {
    time: '4/4',
    treble: ['C5/h@5 C5/q B4/q', 'C5/q G4/q F4/q@1 E4/q@3', 'D4/q C4/h E4/q', 'D4/h E4/h'],
    bass: ['D3/q@5 F3/q G3/h', 'C4/h@1 F3/h', 'G3/w', 'B3/h G3/h'],
  },
  // transcribed from the earlier preview picture
  24: {
    time: '4/4',
    treble: ['C5/h. B4/q', 'A4/h C5/h@5', 'B4/q A4/q G4/q F4/q@1', 'E4/h@3 G4/h'],
    bass: ['E3/h G3/h', 'F3/h E3/h', 'G3/q F3/q E3/q D3/q', 'C3/q G3/q C3/h'],
  },
  // tier 3 phrase 47 of the level's phrase set (current version). The right hand's "5" on
  // G4 in bar 2 is in the current phrase set but not in the old picture.
  25: {
    time: '4/4',
    treble: ['A4/h F4/h', 'G4/h.@5 F4/q', 'G4/h. E4/q', 'C4/w'],
    bass: ['F3/q C4/h@1 A3/q', 'B3/q@2 A3/q@3 G3/q@1 F3/q', 'E3/h C3/h', 'A3/h G3/h'],
  },
  // tier 1 phrase 64 of the level's phrase set
  26: {
    time: '4/4',
    treble: ['C4/h. D4/q', 'E4/h C4/q G4/q', 'D4/w', 'E4/h D4/h'],
    bass: ['r/q F3/q A3/q B3/q', 'A3/w', 'r/q F3/q A3/q C4/q', 'G3/h B3/h'],
  },
};
